import Axios, { AxiosError, InternalAxiosRequestConfig } from 'axios';
import { API_BASE_URL } from './apiBase';
import {
  clearStoredAuth,
  readStoredAccessToken,
  readStoredRefreshToken,
  storeAuthTokens,
} from './features/auth/authStorage';

type RetryableRequestConfig = InternalAxiosRequestConfig & {
  _retry?: boolean;
};

const instance = Axios.create({
  baseURL: API_BASE_URL,
});

let refreshPromise: Promise<string | null> | null = null;

const emitLogout = () => {
  clearStoredAuth();
  window.dispatchEvent(new Event('logout'));
};

const emitAuthTokensUpdated = (payload: {
  token: string;
  refreshToken: string;
}) => {
  window.dispatchEvent(
    new CustomEvent('auth:tokens-updated', {
      detail: payload,
    })
  );
};

const shouldSkipAuthRefresh = (config?: RetryableRequestConfig): boolean => {
  if (!config) {
    return false;
  }
  const headerValue =
    config.headers &&
    (config.headers['x-skip-auth-refresh'] ||
      config.headers['X-Skip-Auth-Refresh']);
  return String(headerValue ?? '') === '1';
};

const isRefreshEndpoint = (url?: string): boolean =>
  typeof url === 'string' && url.includes('/auth/refresh');

const isAuthBootstrapRequest = (url?: string): boolean =>
  typeof url === 'string' &&
  (url.includes('/auth/login') ||
    url.includes('/auth/logout') ||
    url.includes('/auth/mfa/verify') ||
    url.includes('/auth/oauth/exchange'));

const refreshAccessToken = async (): Promise<string | null> => {
  const refreshToken = readStoredRefreshToken();
  if (!refreshToken) {
    emitLogout();
    return null;
  }

  const response = await Axios.post(
    `${API_BASE_URL}/auth/refresh`,
    { refreshToken },
    {
      headers: {
        'x-skip-auth-refresh': '1',
      },
    }
  );

  const accessToken = String(
    response.data?.accessToken || response.data?.token || ''
  ).trim();
  const nextRefreshToken = String(response.data?.refreshToken || '').trim();

  if (!accessToken || !nextRefreshToken) {
    emitLogout();
    return null;
  }

  storeAuthTokens({
    accessToken,
    refreshToken: nextRefreshToken,
  });
  emitAuthTokensUpdated({
    token: accessToken,
    refreshToken: nextRefreshToken,
  });

  return accessToken;
};

instance.interceptors.request.use((config) => {
  const token = readStoredAccessToken();

  if (token) {
    config.headers = config.headers || {};
    config.headers.Authorization = `Bearer ${token}`;
  } else {
    clearStoredAuth();
  }

  return config;
});

instance.interceptors.response.use(
  (response) => response,
  async (error: AxiosError) => {
    const originalRequest = error.config as RetryableRequestConfig | undefined;
    const status = error.response?.status;

    if (
      status !== 401 ||
      !originalRequest ||
      originalRequest._retry ||
      shouldSkipAuthRefresh(originalRequest) ||
      isRefreshEndpoint(originalRequest.url) ||
      isAuthBootstrapRequest(originalRequest.url)
    ) {
      if (status === 401 && (isRefreshEndpoint(originalRequest?.url) || shouldSkipAuthRefresh(originalRequest))) {
        emitLogout();
      }
      return Promise.reject(error);
    }

    try {
      originalRequest._retry = true;

      if (!refreshPromise) {
        refreshPromise = refreshAccessToken().finally(() => {
          refreshPromise = null;
        });
      }

      const nextAccessToken = await refreshPromise;
      if (!nextAccessToken) {
        return Promise.reject(error);
      }

      originalRequest.headers = originalRequest.headers || {};
      originalRequest.headers.Authorization = `Bearer ${nextAccessToken}`;
      return instance.request(originalRequest);
    } catch (refreshError) {
      emitLogout();
      return Promise.reject(refreshError);
    }
  }
);

export default instance;
